import { useEffect } from "react";
import { useLocation, useSearch, Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SEO } from "@/components/seo";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useCart } from "@/lib/cart";

export default function CheckoutSuccess() {
  const searchString = useSearch();
  const sessionId = new URLSearchParams(searchString).get("session_id");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { clearCart } = useCart();

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/checkout/verify", { sessionId });
      return res.json();
    },
    onSuccess: (data) => {
      // Payment confirmed — now safe to clear the cart
      clearCart();
      toast({
        title: "Payment Successful!",
        description: "Your download links are ready.",
      });
      navigate(`/order/${data.id}?code=${data.accessCode}`);
    },
  });

  useEffect(() => {
    if (sessionId && !verifyMutation.isPending && !verifyMutation.isSuccess && !verifyMutation.isError) {
      verifyMutation.mutate();
    }
  }, [sessionId]);

  if (!sessionId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <SEO title="Checkout" description="Checkout session not found." noIndex />
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-8 h-8 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Invalid Checkout Session</h1>
        <p className="text-muted-foreground mb-6">No session ID found.</p>
        <Link href="/">
          <Button data-testid="button-back-home">Back to Home</Button>
        </Link>
      </div>
    );
  }

  if (verifyMutation.isError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <SEO title="Payment Verification" description="Verifying your payment." noIndex />
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
          <XCircle className="w-8 h-8 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Payment Verification Failed</h1>
        <p className="text-muted-foreground mb-6">
          We couldn't verify your payment. If you were charged, please contact support.
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Button onClick={() => verifyMutation.mutate()} data-testid="button-retry-verify">
            Try Again
          </Button>
          <Link href="/">
            <Button variant="outline" data-testid="button-back-home">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <SEO title="Processing Payment" description="Verifying your payment and preparing your downloads." noIndex />
      <Card>
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-2xl font-bold mb-2" data-testid="text-payment-processing">
            Payment Received
          </h1>
          <p className="text-muted-foreground mb-6">
            Verifying your payment and preparing your downloads...
          </p>
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        </CardContent>
      </Card>
    </div>
  );
}
